/**
 * feat-298 — the table of contents as data (spec §3, design NOTES §2).
 *
 * Depth is Track › Lesson › Drill; items are never listed. The TOC MIRRORS the
 * body: a multi-Track Lesson is listed under every Track it belongs to — in full
 * (with its Drills) under its owner Track, and as a childless stub entry
 * elsewhere. Every entry's `target` is the BODY key of the full rendering, so a
 * stub entry jumps to the same place its owner entry does.
 */
import { GroupedCurriculum } from './groupByTrack';
import { drillKey, lessonKey, trackKey } from './reviewKeys';

export type TocLevel = 'track' | 'unassigned' | 'lesson' | 'drill';

export interface TocNode {
  /** Unique within the TOC (a Lesson can appear under several Tracks). */
  id: string;
  /** The body section key a click jumps to (`data-nav-target`). */
  target: string;
  level: TocLevel;
  /** Lesson: position in its Track (stubs count). Drill: position in its Lesson. */
  number?: number;
  emoji?: string;
  /** Canonical English: the TOC is structural chrome. */
  title: string;
  active: boolean;
  /** Set on a stub entry: the title of the Track holding the full rendering. */
  stubOwnerTitle?: string;
  children: TocNode[];
}

function lessonNode(
  prefix: string,
  lesson: { id: string; title: string; emoji: string; active: boolean; drills: Array<{ id: string; title: string; active: boolean }> },
  number: number,
  stubOwnerTitle?: string,
): TocNode {
  const id = `${prefix}${lessonKey(lesson.id)}`;
  return {
    id,
    target: lessonKey(lesson.id),
    level: 'lesson',
    number,
    emoji: lesson.emoji || undefined,
    title: lesson.title,
    active: lesson.active,
    stubOwnerTitle,
    children: stubOwnerTitle
      ? []
      : lesson.drills.map((d, i) => ({
          id: `${prefix}${drillKey(lesson.id, d.id)}`,
          target: drillKey(lesson.id, d.id),
          level: 'drill' as const,
          number: i + 1,
          title: d.title,
          active: d.active,
          children: [],
        })),
  };
}

export function buildToc(grouped: GroupedCurriculum, unassignedTitle: string): TocNode[] {
  if (grouped.mode === 'flat') {
    return grouped.lessons.map((u, i) => lessonNode('', u, grouped.numbers?.[i] ?? i + 1));
  }
  return grouped.groups.map((g) => {
    const tk = trackKey(g.id);
    return {
      id: tk,
      target: tk,
      level: g.kind === 'unassigned' ? 'unassigned' : 'track',
      title: g.kind === 'unassigned' ? unassignedTitle : g.title,
      active: g.active,
      children: g.entries.map((e) =>
        lessonNode(`${tk}/`, e.lesson, e.number, e.kind === 'stub' ? e.ownerTitle : undefined),
      ),
    };
  });
}

/**
 * The TOC node ids from the root down to the NON-stub entry whose target is
 * `key` — the "you are here" path. Stubs never become active (NOTES §2). Empty
 * when nothing matches.
 */
export function tocPathTo(nodes: TocNode[], key: string | null): string[] {
  if (!key) return [];
  for (const n of nodes) {
    if (n.target === key && !n.stubOwnerTitle) return [n.id];
    const sub = tocPathTo(n.children, key);
    if (sub.length) return [n.id, ...sub];
  }
  return [];
}
